import React, { useState, useEffect } from 'react';
import { Box, Popover, Slider, TextField, Button, InputAdornment } from '@mui/material';
import { useSettings } from '../../providers/settings-provider';

interface TolerancePopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    toleranceValue: number;
    onToleranceChange: (value: number) => void;
    onCustomRangeChange?: (min: number, max: number) => void;
    minToleranceAmount?: number;
    maxToleranceAmount?: number;
    provisionAmount?: number;
    customMin?: number | null;
    customMax?: number | null;
}

const TolerancePopover: React.FC<TolerancePopoverProps> = ({
    open,
    anchorEl,
    onClose,
    toleranceValue,
    onToleranceChange,
    onCustomRangeChange,
    minToleranceAmount = 0,
    maxToleranceAmount = 10000,
    provisionAmount = 0,
    customMin = null,
    customMax = null
}) => {
    const { formatAmount } = useSettings();
    const [tempValue, setTempValue] = useState<number>(toleranceValue);
    const [manualMin, setManualMin] = useState<string>('');
    const [manualMax, setManualMax] = useState<string>('');
    const [useManualInput, setUseManualInput] = useState<boolean>(false);

    // Initialize or update from custom range props
    useEffect(() => {
        // An unrestricted range (±Infinity, set by the highlight-to-search
        // "only filter by description" shortcut) has no finite min/max to
        // show - leave the manual fields blank rather than the literal
        // string "Infinity", and let the slider/fields start fresh.
        if (customMin !== null && customMax !== null && Number.isFinite(customMin) && Number.isFinite(customMax)) {
            setManualMin(String(customMin));
            setManualMax(String(customMax));
            setUseManualInput(true);
        } else if (!open) {
            // Only clear when popover is fully closed
            setManualMin('');
            setManualMax('');
            setUseManualInput(false);
        }
    }, [customMin, customMax, open]);

    useEffect(() => {
        setTempValue(toleranceValue);
    }, [toleranceValue]);

    const handleSliderChange = (_: Event, value: number | number[]) => {
        const newValue = value as number;
        setTempValue(newValue);
        // If slider is used again, overwrite manual input and clear custom range
        setManualMin('');
        setManualMax('');
        setUseManualInput(false);
        // Call callback to clear custom range in parent
        if (onCustomRangeChange) {
            // Pass invalid values to signal clearing
            onCustomRangeChange(NaN, NaN);
        }
    };

    const handleManualMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setManualMin(e.target.value);
        setUseManualInput(true);
    };

    const handleManualMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setManualMax(e.target.value);
        setUseManualInput(true);
    };

    const getTolerance = (): number => {
        if (useManualInput && manualMin && manualMax) {
            const min = parseFloat(manualMin);
            const max = parseFloat(manualMax);
            if (!isNaN(min) && !isNaN(max)) {
                // Calculate tolerance from manual min/max
                return (max - min) / 2;
            }
        }
        return tempValue;
    };

    const getFormattedValues = () => {
        const formatted = formatAmount?.(provisionAmount - tempValue);
        const parts = formatted?.split(/(\d+(?:[.,]\d+)?)/) || [];
        const currency = parts[0] || '';
        
        if (useManualInput && manualMin && manualMax) {
            return { min: manualMin, max: manualMax, currency };
        }
        const min = provisionAmount - tempValue;
        const max = provisionAmount + tempValue;
        return { 
            min: String(min), 
            max: String(max),
            currency
        };
    };

    const handleFilter = () => {
        if (useManualInput && manualMin && manualMax) {
            const min = parseFloat(manualMin);
            const max = parseFloat(manualMax);
            if (!isNaN(min) && !isNaN(max) && onCustomRangeChange) {
                onCustomRangeChange(min, max);
            }
        } else {
            onToleranceChange(getTolerance());
        }
        onClose();
    };

    const handleClear = () => {
        setTempValue(0);
        setManualMin('');
        setManualMax('');
        setUseManualInput(false);
        onToleranceChange(0);
        // Same clearing signal handleSliderChange already sends - NaN/NaN
        // tells the parent to drop its own customMin/customMax back to null.
        if (onCustomRangeChange) onCustomRangeChange(NaN, NaN);
        onClose();
    };

    const values = getFormattedValues();

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: 3, minWidth: 300, p: 2, boxShadow: 3 } }}
        >
            <Box px={1} mb={2} mt={5}>
                <Slider
                    min={minToleranceAmount}
                    max={Math.abs(provisionAmount)}
                    value={tempValue}
                    onChange={handleSliderChange}
                    valueLabelDisplay="on"
                    valueLabelFormat={(value) => `± ${value}`}
                    sx={{
                        color: 'var(--color-primary)',
                        '& .MuiSlider-thumb': {
                            backgroundColor: 'var(--color-primary)',
                        },
                        '& .MuiSlider-track': {
                            backgroundColor: 'var(--color-primary)',
                        },
                        '& .MuiSlider-valueLabel': {
                            backgroundColor: 'var(--color-page-background)',
                            color: '#1a1a1aff',
                            fontSize: '12px',
                            fontWeight: 500,
                            boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.3)',
                        }
                    }}
                />
            </Box>
            <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
                <TextField
                    size="small"
                    label="Min"
                    type="number"
                    value={useManualInput ? manualMin : values.min}
                    onChange={handleManualMinChange}
                    InputProps={{
                        startAdornment: <InputAdornment position="start" sx={{ mr: 0.5 }}>{values.currency}</InputAdornment>
                    }}
                    sx={{ flex: 1, background: 'var(--color-page-background)', borderRadius: 2 }}
                />
                <TextField
                    size="small"
                    label="Max"
                    type="number"
                    value={useManualInput ? manualMax : values.max}
                    onChange={handleManualMaxChange}
                    InputProps={{
                        startAdornment: <InputAdornment position="start" sx={{ mr: 0.5 }}>{values.currency}</InputAdornment>
                    }}
                    sx={{ flex: 1, background: 'var(--color-page-background)', borderRadius: 2 }}
                />
            </Box>
            <Box display="flex" justifyContent="space-between" gap={1}>
                <Button
                    variant="outlined"
                    onClick={handleClear}
                    sx={{
                        color: 'var(--color-text-muted)',
                        borderColor: 'var(--color-text-muted)',
                        px: 4,
                        py: 0.5,
                        borderRadius: 2,
                        fontWeight: 600,
                    }}
                >
                    Clear
                </Button>
                <Button
                    variant="contained"
                    onClick={handleFilter}
                    sx={{
                        backgroundColor: 'var(--color-primary)',
                        color: '#fff',
                        px: 4,
                        py: 0.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: 'var(--color-primary)',
                        }
                    }}
                >
                    Filter
                </Button>
            </Box>
        </Popover>
    );
};

export default TolerancePopover;